import { makeAutoObservable, runInAction } from 'mobx'

import { userManager } from '@/Core/Managers'

/** Код опції режиму підказок. Бек приймає лише `[a-z0-9_-]{1,50}`. */
export const TUTORIAL_MODE_OPTION = 'tutorial_mode'

/** Код опції зі списком підказок, які користувач прибрав поштучно. */
export const TUTORIAL_HIDDEN_OPTION = 'tutorial_hidden'

/**
 * Режим підказок увімкнений, поки користувач не вимкнув його сам.
 *
 * Нова людина бачить пояснення з першого дня, а не шукає, де їх увімкнути.
 * Той, кому вони не потрібні, вимикає режим один раз — і це зберігається на
 * акаунті, а не в браузері.
 */
const DEFAULT_ENABLED = true

/**
 * Не частіше за це перечитуємо стан при поверненні на вкладку, мс: фокус
 * буває кілька разів поспіль (діалог, перемикання вікон), а запит на цьому
 * стенді недешевий. Секунди досить, щоб устигнути прибрати підказку деінде
 * й повернутися.
 */
const REFRESH_THROTTLE_MS = 1000

/**
 * Хто зараз увійшов — опції підказок належать акаунту. Ідентифікатор той
 * самий, яким ядро підписує свій кеш опцій у `localStorage`.
 */
const currentUser = (): string => String(userManager.user['uuid'] ?? userManager.user.id ?? '')

/** Опції, з яких складається стан підказок. */
const TUTORIAL_OPTIONS = [TUTORIAL_MODE_OPTION, TUTORIAL_HIDDEN_OPTION]

/**
 * Режим підказок: стан на акаунті, один запит на сторінку.
 *
 * ⚠️ Власної таблиці під це немає й не треба: ядро вже має опції користувача
 * (`GET|POST /services/user/options/{code}`, значення — JSON). Заводити свою
 * сутність заради двох полів означало б другу копію механізму, який доведеться
 * тримати в синхроні з ядровим.
 *
 * ⚠️ Стан живе на **акаунті**, а не в `localStorage`: людина, яка вимкнула
 * підказки на робочому місці, не має вмикати їх наново з ноутбука. Ядровий
 * `UserManager` при цьому кешує опції в `localStorage` **у розрізі
 * користувача**, тож повторне відкриття сторінки не блимає.
 *
 * ⚠️ Завантаження дедуплікується. Підказок на сторінці буває десяток, і кожна
 * кличе `load()` у своєму `useEffect` — без склеювання це був би десяток
 * однакових запитів.
 */
class TutorialManager {
    /**
     * Для кого опції вже приїхали з сервера (або їх там немає).
     *
     * ⚠️ Саме «для кого», а не прапорець на всю сесію: після виходу й входу
     * іншим акаунтом без перезавантаження сторінки прапорець лишався
     * піднятим, і стан нового користувача з сервера не читався взагалі.
     */
    private loadedFor: string | null = null

    /** Запит у польоті — щоб десять підказок дали один запит, а не десять. */
    private loading: Promise<void> | null = null

    /** Коли востаннє перечитували стан при поверненні на вкладку. */
    private refreshedAt = 0

    constructor() {
        makeAutoObservable(this)

        // Прибрати чи повернути підказки могли в іншій вкладці, а ця читає стан
        // із сервера один раз: відкритий у ній профіль писав «Ви ще жодної не
        // прибрали» з неактивною «Показати знову», доки сторінку не
        // перезавантажать. Інша вкладка того ж браузера пише кеш опцій ядра в
        // `localStorage`, і браузер повідомляє про це решту вкладок — звідти
        // нове значення й беремо, одразу й без запиту
        window.addEventListener('storage', (event) => this.applyFromOtherTab(event))

        // Інший браузер чи пристрій спільного `localStorage` не має — для нього
        // стан перечитується з сервера, коли вкладка знову у фокусі
        window.addEventListener('focus', () => this.refresh())
    }

    private get isLoaded(): boolean {
        return this.loadedFor !== null && this.loadedFor === currentUser()
    }

    /**
     * Чи можна вже вирішувати, показувати підказки.
     *
     * Поки невідомо — не показуємо нічого. Інакше той, хто режим вимкнув,
     * бачив би спалах підказок на кожному відкритті сторінки.
     *
     * Значення з кешу ядра вважаємо достатнім: воно вже прочитане з
     * `localStorage` цього ж користувача, тож чекати на мережу нема потреби.
     */
    get isReady(): boolean {
        return this.isLoaded || userManager.getUserOption(TUTORIAL_MODE_OPTION) !== undefined
    }

    get isEnabled(): boolean {
        return userManager.getUserOption<boolean>(TUTORIAL_MODE_OPTION, DEFAULT_ENABLED) !== false
    }

    /** Підказки, прибрані поштучно. */
    get hidden(): string[] {
        const value = userManager.getUserOption<string[]>(TUTORIAL_HIDDEN_OPTION, [])

        // Значення приходить із сервера як довільний JSON: зіпсований запис не
        // має валити кожну сторінку з підказкою
        return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
    }

    /** Чи показувати конкретну підказку. */
    shows(id: string): boolean {
        return this.isReady && this.isEnabled && !this.hidden.includes(id)
    }

    /** Підтягнути стан із сервера. Безпечно кликати з кожної підказки. */
    load(): Promise<void> {
        if (this.isLoaded) return Promise.resolve()

        const user = currentUser()

        // `runInAction` обов'язковий: `isLoaded` читає `isReady`, який читає
        // компонент, а зміна спостережуваного поля поза дією в MobX — попередження
        this.loading ??= userManager.loadUserOptions(TUTORIAL_OPTIONS).finally(() => {
            runInAction(() => {
                this.loadedFor = user
                this.loading = null
            })
        })

        return this.loading
    }

    /**
     * Перечитати стан із сервера, коли вкладка знову у фокусі.
     *
     * Лише якщо в цій вкладці стан уже читали — на сторінці були підказки чи
     * відкритий профіль. Інакше нічого показувати й нема, а на екрані входу
     * запит без токена тільки смикнув би сервер.
     */
    refresh(): void {
        if (!userManager.isAuthorized || !this.isLoaded) return
        if (Date.now() - this.refreshedAt < REFRESH_THROTTLE_MS) return

        this.refreshedAt = Date.now()
        void userManager.loadUserOptions(TUTORIAL_OPTIONS)
    }

    /**
     * Узяти стан підказок, записаний іншою вкладкою.
     *
     * ⚠️ Саме зі сховища, а не повторним запитом: подія приходить одразу після
     * запису в кеш, а POST тієї вкладки на сервер ще може бути в дорозі — GET
     * повернув би старе значення.
     */
    private applyFromOtherTab(event: StorageEvent): void {
        const user = currentUser()
        if (!event.key?.startsWith('UserManagerOptions') || !user || !event.key.endsWith(`_${user}`)) return

        let stored: Record<string, unknown>
        try {
            stored = JSON.parse(event.newValue ?? '{}') as Record<string, unknown>
        } catch {
            return
        }

        for (const code of TUTORIAL_OPTIONS) {
            const value = stored[code]
            if (value === undefined) continue
            if (JSON.stringify(value) === JSON.stringify(userManager.getUserOption(code))) continue

            // Лише в кеш: на сервер це значення вже записала інша вкладка
            void userManager.setUserOption(code, value, false)
        }
    }

    async setEnabled(value: boolean): Promise<void> {
        await userManager.setUserOption(TUTORIAL_MODE_OPTION, value)
    }

    /** Прибрати одну підказку назавжди. */
    async dismiss(id: string): Promise<void> {
        if (this.hidden.includes(id)) return

        await userManager.setUserOption(TUTORIAL_HIDDEN_OPTION, [...this.hidden, id])
    }

    /** Повернути одну прибрану підказку — решта лишаються прибраними. */
    async restore(id: string): Promise<void> {
        if (!this.hidden.includes(id)) return

        await userManager.setUserOption(
            TUTORIAL_HIDDEN_OPTION,
            this.hidden.filter((item) => item !== id)
        )
    }

    /**
     * Повернути всі прибрані підказки.
     *
     * ⚠️ Обов'язкова пара до поштучного «більше не показувати». Без неї
     * закрита підказка зникає безповоротно, і повернути її можна хіба що через
     * базу — а користувач про це не знає й вирішить, що фіча зламалася.
     */
    async restoreAll(): Promise<void> {
        await userManager.setUserOption(TUTORIAL_HIDDEN_OPTION, [])
    }
}

export const tutorialManager = new TutorialManager()
